/**
 * Copyright (c) 2018-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { Structure, StructureElement, Unit } from '../../../mol-model/structure';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { createLinkCylinderMesh, LinkCylinderParams } from './util/link';
import { UnitsMeshParams } from '../units-visual';
import { ComplexMeshVisual } from '../complex-visual';
import { LocationIterator } from '../../../mol-geo/util/location-iterator';
import { OrderedSet, Interval } from '../../../mol-data/int';
import { EmptyLoci } from '../../../mol-model/loci';
import { getElementIdx, MetalsSet } from '../../../mol-model/structure/structure/unit/bonds/common';
import { getAltResidueLociFromId, getAltResidueLoci } from './util/common';
function createCarbohydrateTerminalLinkCylinderMesh(ctx, structure, theme, props, mesh) {
    var _a = structure.carbohydrates, terminalLinks = _a.terminalLinks, elements = _a.elements;
    var terminalLinkSizeFactor = props.terminalLinkSizeFactor;
    var location = StructureElement.Location.create(structure);
    var builderProps = {
        linkCount: terminalLinks.length,
        position: function (posA, posB, edgeIndex) {
            var l = terminalLinks[edgeIndex];
            if (l.fromCarbohydrate) {
                Vec3.copy(posA, elements[l.carbohydrateIndex].geometry.center);
                l.elementUnit.conformation.position(l.elementUnit.elements[l.elementIndex], posB);
            }
            else {
                l.elementUnit.conformation.position(l.elementUnit.elements[l.elementIndex], posA);
                Vec3.copy(posB, elements[l.carbohydrateIndex].geometry.center);
            }
        },
        radius: function (edgeIndex) {
            var l = terminalLinks[edgeIndex];
            if (l.fromCarbohydrate) {
                var carb = elements[l.carbohydrateIndex];
                var ring = carb.unit.rings.all[carb.ringIndex];
                location.unit = carb.unit;
                location.element = carb.unit.elements[ring[0]];
            }
            else {
                location.unit = l.elementUnit;
                location.element = l.elementUnit.elements[l.elementIndex];
            }
            return theme.size.size(location) * terminalLinkSizeFactor;
        },
        style: function (edgeIndex) {
            var l = terminalLinks[edgeIndex];
            var eI = l.elementUnit.elements[l.elementIndex];
            var beI = getElementIdx(l.elementUnit.model.atomicHierarchy.atoms.type_symbol.value(eI));
            return MetalsSet.has(beI) ? 1 /* Dashed */ : 0 /* Solid */;
        }
    };
    return createLinkCylinderMesh(ctx, builderProps, props, mesh);
}
export var CarbohydrateTerminalLinkParams = __assign(__assign(__assign({}, UnitsMeshParams), LinkCylinderParams), { terminalLinkSizeFactor: PD.Numeric(0.2, { min: 0, max: 3, step: 0.01 }) });
export function CarbohydrateTerminalLinkVisual(materialId) {
    return ComplexMeshVisual({
        defaultProps: PD.getDefaultValues(CarbohydrateTerminalLinkParams),
        createGeometry: createCarbohydrateTerminalLinkCylinderMesh,
        createLocationIterator: CarbohydrateTerminalLinkIterator,
        getLoci: getTerminalLinkLoci,
        eachLocation: eachTerminalLink,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.terminalLinkSizeFactor !== currentProps.terminalLinkSizeFactor ||
                newProps.radialSegments !== currentProps.radialSegments ||
                newProps.linkCap !== currentProps.linkCap);
        }
    }, materialId);
}
function CarbohydrateTerminalLinkIterator(structure) {
    var _a = structure.carbohydrates, elements = _a.elements, terminalLinks = _a.terminalLinks;
    var groupCount = terminalLinks.length;
    var instanceCount = 1;
    var location = StructureElement.Location.create(structure);
    var getLocation = function (groupIndex) {
        var terminalLink = terminalLinks[groupIndex];
        if (terminalLink.fromCarbohydrate) {
            var carb = elements[terminalLink.carbohydrateIndex];
            var ring = carb.unit.rings.all[carb.ringIndex];
            location.unit = carb.unit;
            location.element = carb.unit.elements[ring[0]];
        }
        else {
            location.unit = terminalLink.elementUnit;
            location.element = terminalLink.elementUnit.elements[terminalLink.elementIndex];
        }
        return location;
    };
    return LocationIterator(groupCount, instanceCount, 1, getLocation, true);
}
function getTerminalLinkLoci(pickingId, structure, id) {
    var objectId = pickingId.objectId, groupId = pickingId.groupId;
    if (id === objectId) {
        var _a = structure.carbohydrates, terminalLinks = _a.terminalLinks, elements = _a.elements;
        var l = terminalLinks[groupId];
        var carb = elements[l.carbohydrateIndex];
        return StructureElement.Loci.union(getAltResidueLociFromId(structure, carb.unit, carb.residueIndex, carb.altId), getAltResidueLoci(structure, l.elementUnit, l.elementUnit.elements[l.elementIndex]));
    }
    return EmptyLoci;
}
function eachTerminalLink(loci, structure, apply) {
    var changed = false;
    if (!StructureElement.Loci.is(loci))
        return false;
    if (!Structure.areEquivalent(loci.structure, structure))
        return false;
    var getTerminalLinkIndices = structure.carbohydrates.getTerminalLinkIndices;
    var _loop_1 = function (unit, indices) {
        if (!Unit.isAtomic(unit))
            return "continue";
        OrderedSet.forEach(indices, function (v) {
            // TODO avoid duplicate calls to apply
            var linkIndices = getTerminalLinkIndices(unit, unit.elements[v]);
            for (var i = 0, il = linkIndices.length; i < il; ++i) {
                if (apply(Interval.ofSingleton(linkIndices[i])))
                    changed = true;
            }
        });
    };
    for (var _i = 0, _a = loci.elements; _i < _a.length; _i++) {
        var _b = _a[_i], unit = _b.unit, indices = _b.indices;
        _loop_1(unit, indices);
    }
    return changed;
}
//# sourceMappingURL=carbohydrate-terminal-link-cylinder.js.map